import { useEffect, useMemo, useRef, useState } from "react"
import { assignResearchDeploymentAccountRun } from "./virtualFarmerApi"
import { usePortalLanguage } from "./virtualFarmerI18n"

const positiveId = value => Number.isSafeInteger(value) && value > 0
const domains = new Set(["aeroponic_stm32", "virtual_farmer_vwc", "research_observation_only"])
const nonBlank = value => typeof value === "string" && value.trim().length > 0
const uniqueId = (items, id) => items.filter(item => item?.id === id).length === 1
const utcTime = value => {
     if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|\+00:00)$/.test(value)) return null
     const time = Date.parse(value)
     return Number.isFinite(time) && new Date(time).toISOString().slice(0, 19) === value.slice(0, 19) ? time : null
}

// Candidates are factual saved relationships, never inferred from the selected
// portal location, device-bound legacy runs, labels, or a tenant/browser header.
export const deploymentRunCandidates = (research, locations, accountId) => {
     if (!positiveId(accountId) || !Array.isArray(research?.deployments) || !Array.isArray(research?.account_runs) || !Array.isArray(locations)) return []
     const allDeployments = research.deployments
     const allRuns = research.account_runs
     const locationFor = externalId => {
          const matches = locations.filter(location => typeof location?.id === "string" && location.id === externalId)
          return matches.length === 1 && nonBlank(matches[0].name) ? matches[0] : null
     }
     return allDeployments.filter(deployment => positiveId(deployment?.id)
          && deployment.account_id === accountId && uniqueId(allDeployments, deployment.id)
          && deployment.ended_at === null && deployment.account_run_id === null
          && domains.has(deployment.control_domain) && nonBlank(deployment.label)
          && typeof deployment.external_location_id === "string" && utcTime(deployment.started_at) !== null)
          .map(deployment => ({ deployment, runs: allRuns.filter(run => positiveId(run?.id)
               && run.account_id === accountId && uniqueId(allRuns, run.id)
               && run.status === "active" && run.ended_at === null && nonBlank(run.name)
               && nonBlank(run.external_location_id) && locationFor(run.external_location_id)
               && utcTime(run.started_at) !== null && utcTime(run.started_at) <= utcTime(deployment.started_at)
               && (deployment.external_location_id === "" || deployment.external_location_id === run.external_location_id)
               && !allDeployments.some(other => other?.id !== deployment.id && other?.account_run_id === run.id
                    && other.ended_at === null && other.control_domain === deployment.control_domain))
               .map(run => ({ run, location: locationFor(run.external_location_id) })) }))
          .filter(candidate => candidate.runs.length > 0)
}

export default function DeploymentRunAssignment(props) {
     // Replacing the account also replaces selection, locks and request scope.
     return <AssignmentForm key={positiveId(props.accountId) ? props.accountId : "unauthenticated"} {...props} />
}

function AssignmentForm({ research, locations, accountId, enabled = false, loading = false, error = false, onRefresh }) {
     const { t } = usePortalLanguage()
     const [deploymentId, setDeploymentId] = useState("")
     const [runId, setRunId] = useState("")
     const [confirmed, setConfirmed] = useState(false)
     const [busy, setBusy] = useState(false)
     const [outcome, setOutcome] = useState("")
     const [failure, setFailure] = useState("")
     const scope = useRef({ mounted: true, locked: false, controller: null })
     const candidates = useMemo(() => deploymentRunCandidates(research, locations, accountId), [research, locations, accountId])
     const selected = candidates.find(candidate => String(candidate.deployment.id) === deploymentId)
     const selectedRun = selected?.runs.find(candidate => String(candidate.run.id) === runId)
     const available = enabled && positiveId(accountId) && !loading && !error

     useEffect(() => {
          const current = scope.current
          current.mounted = true
          return () => { current.mounted = false; current.controller?.abort() }
     }, [])
     useEffect(() => { setConfirmed(false) }, [research])

     const submit = async event => {
          event.preventDefault()
          const current = scope.current
          if (current.locked || !available || !selected || !selectedRun || !confirmed || typeof onRefresh !== "function") return
          current.locked = true
          current.controller = new AbortController()
          setBusy(true); setOutcome(""); setFailure("")
          try {
               const result = await assignResearchDeploymentAccountRun(selected.deployment.id, selectedRun.run.id, { signal: current.controller.signal })
               if (!current.mounted) return
               if (result?.ok !== true || result.deployment_id !== selected.deployment.id || result.account_run_id !== selectedRun.run.id) {
                    setFailure("uncertain")
                    await onRefresh()
                    return
               }
               // A confirmed write is independent of the following read. The
               // table only changes once a new server overview is received.
               setOutcome("saved")
               setConfirmed(false)
               let refreshed
               try { refreshed = await onRefresh() } catch (_) { refreshed = { refreshed: false } }
               if (current.mounted && !refreshed?.refreshed) setOutcome("savedRefreshFailed")
          } catch (requestError) {
               if (!current.mounted || requestError.name === "AbortError") return
               setConfirmed(false)
               setFailure(requestError.status === 409 ? "conflict" : [401, 403, 404, 422].includes(requestError.status) ? "notAllowed" : "uncertain")
               // A read may resolve a concurrent conflict or an uncertain
               // network outcome; it must never repeat the POST itself.
               try { await onRefresh() } catch (_) { /* Keep the original truthful mutation result. */ }
          } finally {
               current.locked = false; current.controller = null
               if (current.mounted) setBusy(false)
          }
     }
     const selectDeployment = value => { setDeploymentId(value); setRunId(""); setConfirmed(false); setOutcome(""); setFailure("") }
     const selectRun = value => { setRunId(value); setConfirmed(false); setOutcome(""); setFailure("") }

     return <section className="vf-card" data-testid="deployment-run-assignment">
          <div className="vf-card-heading"><div><h3>{t("assignment.title")}</h3><p>{t("assignment.intro")}</p></div></div>
          {outcome && <p role="status">{t(`assignment.${outcome}`)}</p>}
          {failure && <p className="vf-error-banner" role="alert">{t(`assignment.error.${failure}`)}</p>}
          {!available ? <p role="status">{t("assignment.unavailable")}</p> : !candidates.length ? <p className="vf-inline-empty">{t("assignment.empty")}</p> : <form className="vf-research-form" onSubmit={submit}>
               <label>{t("assignment.deployment")}<select value={selected ? deploymentId : ""} disabled={busy} onChange={event => selectDeployment(event.target.value)}>
                    <option value="">{t("assignment.select")}</option>{candidates.map(({ deployment }) => <option key={deployment.id} value={deployment.id}>{deployment.label} · {t(`research.controlDomain.${deployment.control_domain}`)}</option>)}
               </select></label>
               <label>{t("mapping.run")}<select value={selectedRun ? runId : ""} disabled={busy || !selected} onChange={event => selectRun(event.target.value)}>
                    <option value="">{t("assignment.select")}</option>{selected?.runs.map(({ run, location }) => <option key={run.id} value={run.id}>{run.name} · {location.name}</option>)}
               </select></label>
               {selectedRun && <label className="vf-checkbox"><input type="checkbox" checked={confirmed} disabled={busy} onChange={event => setConfirmed(event.target.checked)} /><span>{t("assignment.confirm", { deployment: selected.deployment.label, run: selectedRun.run.name, location: selectedRun.location.name })}{selected.deployment.external_location_id === "" && <small>{t("assignment.locationSet")}</small>}</span></label>}
               <button type="submit" className="vf-primary-button" disabled={busy || !selectedRun || !confirmed}>{t(busy ? "assignment.saving" : "assignment.save")}</button>
          </form>}
          <p className="vf-trend-note">{t("assignment.boundary")}</p>
     </section>
}
